import { CommonModule } from '@angular/common';
import {
  Component,
  ContentChild,
  EventEmitter,
  HostAttributeToken,
  inject,
  Input,
  Output,
  TemplateRef,
} from '@angular/core';
import { ReactiveFormsModule } from '@angular/forms';
import { RadioButtonModule } from 'primeng/radiobutton';
import { getUniqueId } from '../../../functions/get-unique-id';
import { BaseControlComponent } from './base-control.component';
import { HelpTextComponent } from './help-text.component';

@Component({
  selector: 'app-radio-button',
  imports: [
    ReactiveFormsModule,
    CommonModule,
    RadioButtonModule,
    HelpTextComponent,
  ],
  template: `
    <fieldset class="tw-pb-1 tw-px-0.5">
      <legend
        [id]="legendId"
        [class.tw-sr-only]="legendIsSrOnly"
        [class.tw-font-bold]="!legendIsSrOnly"
      >
        {{ groupLabel }}{{ showRequiredOnLegend ? ' *' : '' }}
      </legend>
      <div class="tw-flex tw-items-center tw-px-0.5 tw-pt-1">
        <div
          class="tw-flex tw-px-0.5"
          [class]="classes"
          [ngClass]="{
            'tw-flex-col tw-gap-2': vertical,
            'tw-flex-row tw-flex-wrap tw-gap-y-2': !vertical,
          }"
        >
          @for (
            option of options;
            track option[optionValue];
            let index = $index
          ) {
            <div class="tw-flex tw-items-center tw-pr-5">
              <p-radiobutton
                [inputId]="getOptionInputId(index)"
                [value]="option[optionValue]"
                [formControl]="control"
                [name]="name"
                [ariaLabelledBy]="getOptionLabelledBy(index)"
                [tabindex]="0"
              />
              @if (labelTemplate) {
                <label
                  [id]="getOptionLabelId(index)"
                  [for]="getOptionInputId(index)"
                  class="tw-cursor-pointer"
                >
                  <ng-container
                    *ngTemplateOutlet="
                      labelTemplate;
                      context: { $implicit: option }
                    "
                  ></ng-container>
                </label>
              } @else {
                <label
                  [id]="getOptionLabelId(index)"
                  [for]="getOptionInputId(index)"
                  class="tw-ml-2 tw-mt-0.5 tw-cursor-pointer"
                  >{{ option[optionLabel] }}</label
                >
              }
            </div>
          }
        </div>

        @if (showClear && !vertical && control.value !== null) {
          <button
            type="button"
            class="tw-flex tw-ml-0.5 tw-pt-0.5 tw-text-gray-400 hover:tw-text-gray-600"
            aria-label="Clear"
            (click)="onClear()"
          >
            <i class="pi pi-times" aria-hidden="true"></i>
          </button>
        }
      </div>

      @if (showHelpText) {
        @if (vertical) {
          <app-help-text
            [control]="control"
            [helpText]="helpText"
            [label]="label"
          />
        } @else {
          <div class="tw-px-0.5">
            <app-help-text
              [control]="control"
              [helpText]="helpText"
              [label]="label"
            />
          </div>
        }
      }
    </fieldset>
  `,
  styles: ``,
})
export class RadioButtonComponent extends BaseControlComponent {
  private readonly hostAriaLabel = inject(
    new HostAttributeToken('aria-label'),
    { optional: true },
  );

  @ContentChild('label')
  labelTemplate: TemplateRef<any>;

  @Input({ required: true })
  optionLabel: string;

  @Input({ required: true })
  optionValue: string;

  /**
   * Optional field name to use for aria-label/title; falls back to optionLabel.
   */
  @Input()
  optionTitle?: string;

  @Input({ required: true })
  options: any[];

  /**
   * Set to false for horizontal layout. Default is true.
   */
  @Input()
  vertical = true;

  @Input()
  showClear = false;

  /**
   * When true, the legend is visually hidden (sr-only) but remains accessible to screen readers.
   * Use this when the radio button is wrapped in a component that already displays a visible label.
   */
  @Input()
  srOnlyLegend = false;

  @Input()
  classes: string;

  /** Screen reader only label - used when no visible label but accessibility name is needed */
  @Input()
  screenReaderLabel: string;

  /**
   * Prefixes each option's accessible name with the group label, e.g. "Auto-fit column width Yes".
   * Use when the group label must be announced with every option, not only when focus enters the group.
   */
  @Input()
  announceGroupLabelWithOptions = false;

  @Output()
  clear = new EventEmitter();

  protected name = getUniqueId('app-radio-button');

  protected get legendId(): string {
    return `${this.id}-legend`;
  }

  protected get groupLabel(): string {
    return (
      this.label?.trim() ||
      this.screenReaderLabel?.trim() ||
      this.hostAriaLabel?.trim() ||
      'Options'
    );
  }

  protected get legendIsSrOnly(): boolean {
    return this.srOnlyLegend || !this.label?.trim();
  }

  protected get showRequiredOnLegend(): boolean {
    return this.required && !!this.label?.trim() && !this.srOnlyLegend;
  }

  protected getOptionInputId(index: number): string {
    return `${this.id}-${index}`;
  }

  protected getOptionLabelId(index: number): string {
    return `${this.id}-label-${index}`;
  }

  protected getOptionLabelledBy(index: number): string {
    const labelId = this.getOptionLabelId(index);

    if (this.announceGroupLabelWithOptions) {
      return `${this.legendId} ${labelId}`;
    }

    return labelId;
  }

  protected onClear() {
    this.control.setValue(null);
    this.control.markAsDirty();
    this.clear.emit();
  }
}
